import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  IconButton,
  Typography,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Card,
  CardMedia,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import firebase from '../utilities/firebase';
import constants from '../utilities/constants';
import { StateManager } from '../utilities/stateManager';
import algolia from '../utilities/algolia';
import CarSelectList from './CarSelectList';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../utilities/ModalStackContext';

const AddLeadDialog = ({ open, onClose, onSuccess }) => {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [formData, setFormData] = useState({
    type: 'buy', // Default to 'buy'
    name: '',
    email: '',
    phone: '',
    source: '',
    carsOwned: [],
    carsInterested: [],
    stock: '', // Car they want to buy (stock number only)
    // Sell lead fields
    year: '',
    make: '',
    model: '',
    condition: [], // Array: museum, show, driver, project
    desiredPrice: '',
    thumbnail: '',
    notes: '',
    date: moment().format('YYYY-MM-DD'),
    status: constants.lead_statuses[0], // 'pending contact'
    assigned: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [newCarOwned, setNewCarOwned] = useState('');
  const [newCarInterested, setNewCarInterested] = useState('');
  
  // Car search states
  const [carSearchValue, setCarSearchValue] = useState('');
  const [carSearchResults, setCarSearchResults] = useState([]);
  const [selectedCarIndex, setSelectedCarIndex] = useState(0);
  const [selectedCar, setSelectedCar] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setFormData({
        type: 'buy', // Default to 'buy'
        name: '',
        email: '',
        phone: '',
        source: '',
        carsOwned: [],
        carsInterested: [],
        stock: '',
        // Sell lead fields
        year: '',
        make: '',
        model: '',
        condition: [],
        desiredPrice: '',
        thumbnail: '',
        notes: '',
        date: moment().format('YYYY-MM-DD'),
        status: constants.lead_statuses[0],
        assigned: false,
      });
      setErrors({});
      setNewCarOwned('');
      setNewCarInterested('');
      setCarSearchValue('');
      setCarSearchResults([]);
      setSelectedCarIndex(0);
      setSelectedCar(null);
      setThumbnailError(false);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    // Reset thumbnail error when URL changes
    if (name === 'thumbnail') {
      setThumbnailError(false);
    }
  };

  const addCarOwned = () => {
    if (newCarOwned.trim()) {
      setFormData(prev => ({
        ...prev,
        carsOwned: [...prev.carsOwned, newCarOwned.trim()]
      }));
      setNewCarOwned('');
    }
  };

  const removeCarOwned = (index) => {
    setFormData(prev => ({
      ...prev,
      carsOwned: prev.carsOwned.filter((_, i) => i !== index)
    }));
  };

  const addCarInterested = () => {
    if (newCarInterested.trim()) {
      setFormData(prev => ({
        ...prev,
        carsInterested: [...prev.carsInterested, newCarInterested.trim()]
      }));
      setNewCarInterested('');
    }
  };

  const removeCarInterested = (index) => {
    setFormData(prev => ({
      ...prev,
      carsInterested: prev.carsInterested.filter((_, i) => i !== index)
    }));
  };

  // Car search functionality
  const handleCarSearch = async (e) => {
    const { value } = e.target;
    setCarSearchValue(value);

    if (value === '') {
      setCarSearchResults([]);
      return;
    }

    try {
      const index = algolia.client.initIndex('cars');
      const { hits } = await index.search(value);
      const results = hits.map(hit => ({
        ...hit,
        action: () => selectCar(hit),
        label: getCarLabel(hit)
      }));
      
      // Sort results by stock number (largest to smallest)
      results.sort((a, b) => {
        // Extract numeric stock numbers, removing all non-numeric characters
        const stockA = a.stock ? parseInt(a.stock.replace(/\D/g, ''), 10) : 0;
        const stockB = b.stock ? parseInt(b.stock.replace(/\D/g, ''), 10) : 0;
        
        // Sort from largest to smallest
        return stockB - stockA;
      });
      
      setCarSearchResults(results);
    } catch (error) {
      console.error('Error searching cars:', error);
      setCarSearchResults([]);
    }
  };

  const selectCar = (hit) => {
    const displayName = `${hit.stock || ""}${!!hit.stock ? " " : ""}${hit.year || ""}${!!hit.year ? " " : ""}${hit.make || ""}${!!hit.make ? " " : ""}${hit.model || ""}`;
    setCarSearchValue(displayName);
    setSelectedCar(hit);
    setFormData(prev => ({ ...prev, stock: hit.stock || '' }));
    setCarSearchResults([]);
  };

  const clearSelectedCar = () => {
    setCarSearchValue('');
    setSelectedCar(null);
    setFormData(prev => ({ ...prev, stock: '' }));
    setCarSearchResults([]);
  };

  const handleCarKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (carSearchResults.length > 0 && selectedCarIndex < carSearchResults.length) {
        const selectedResult = carSearchResults[selectedCarIndex];
        if (selectedResult.action) {
          selectedResult.action();
        }
      }
    }

    if (e.key === 'Escape') {
      setCarSearchResults([]);
      return;
    }

    let newIndex = selectedCarIndex;
    if (e.key === 'ArrowUp') newIndex -= 1;
    if (e.key === 'ArrowDown') newIndex += 1;
    newIndex = Math.max(0, Math.min(carSearchResults.length - 1, newIndex));
    setSelectedCarIndex(newIndex);
  };

  const getCarLabel = (hit) => {
    // Use highlights if available, otherwise fall back to regular fields
    if (hit._highlightResult) {
      const highlights = hit._highlightResult;
      const year = highlights.year?.value || hit.year || "";
      const make = highlights.make?.value || hit.make || "";
      const model = highlights.model?.value || hit.model || "";
      const stock = highlights.stock?.value || hit.stock || "";
      
      // Convert <em> tags to <strong> tags for bold highlighting
      const boldYear = year.replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
      const boldMake = make.replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
      const boldModel = model.replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
      const boldStock = stock.replace(/<em>/g, '<strong>').replace(/<\/em>/g, '</strong>');
      
      const name = `${boldStock} - ${boldYear} ${boldMake} ${boldModel}`.trim();
      return name || `${hit.stock || ""} - ${hit.year || ""} ${hit.make || ""} ${hit.model || ""}`.trim();
    } else {
      // Fallback to regular fields
      return `${hit.stock || ""} - ${hit.year || ""} ${hit.make || ""} ${hit.model || ""}`.trim();
    }
  };

  const handleConditionChange = (conditionValue) => {
    setFormData(prev => {
      const currentCondition = prev.condition || [];
      const isSelected = currentCondition.includes(conditionValue);
      
      if (isSelected) {
        return {
          ...prev,
          condition: currentCondition.filter(c => c !== conditionValue)
        };
      } else {
        return {
          ...prev,
          condition: [...currentCondition, conditionValue]
        };
      }
    });
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Email/phone validation - only required for buy leads
    if (formData.type === 'buy') {
      if (!formData.email.trim() && !formData.phone.trim()) {
        newErrors.email = 'Either email or phone number is required';
        newErrors.phone = 'Either email or phone number is required';
      }
    }
    
    // Validate email format if provided (for both types)
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Validate phone format if provided (for both types)
    if (formData.phone.trim() && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.source.trim()) {
      newErrors.source = 'Source is required';
    }

    // Validate sell lead specific fields
    if (formData.type === 'sell') {
      if (!formData.year.trim()) {
        newErrors.year = 'Year is required';
      }
      if (!formData.make.trim()) {
        newErrors.make = 'Make is required';
      }
      if (!formData.model.trim()) {
        newErrors.model = 'Model is required';
      }
      if (!formData.condition || formData.condition.length === 0) {
        newErrors.condition = 'At least one condition must be selected';
      }
      // Validate thumbnail URL if provided
      if (formData.thumbnail.trim() && !/^https?:\/\/.+/.test(formData.thumbnail.trim())) {
        newErrors.thumbnail = 'Please enter a valid URL (must start with http:// or https://)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const db = firebase.firestore();
      
      // Clean phone number
      const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      
      // Normalize notes to array of objects per ContactHistory.js convention
      const initialNotes = (formData.notes || '').trim()
        ? [{
            id: Date.now().toString(),
            text: (formData.notes || '').trim(),
            timestamp: moment().format('YYYY/MM/DD HH:mm'),
            user: StateManager.userName || 'Unknown'
          }]
        : [];
      
      // Build lead data based on type
      let leadData = {
        ...formData,
        assigned: formData.sales_id ? true : false,
        phone: cleanPhone,
        notes: initialNotes,
        sortDate: moment().format('YYYY-MM-DD-HH-mm-ss'),
        source: formData.source || "manual",
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      };

      // For sell leads, format the car field and clean up unused fields
      if (formData.type === 'sell') {
        leadData.car = `${formData.year} ${formData.make} ${formData.model}`.trim();
        // Remove buy-specific fields
        delete leadData.stock;
        delete leadData.carsOwned;
        delete leadData.carsInterested;
      } else {
        // For buy leads, remove sell-specific fields
        delete leadData.year;
        delete leadData.make;
        delete leadData.model;
        delete leadData.condition;
        delete leadData.desiredPrice;
        delete leadData.thumbnail;
      }

      const docRef = await db.collection('leads').add(leadData);
      
      console.log('Lead added successfully:', docRef.id);
      
      if (onSuccess) {
        onSuccess(docRef.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error adding lead:', error);
      setErrors({ submit: 'Failed to add lead. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: myZ }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6">Add New Lead</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="name"
              label="Full Name *"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              error={!!errors.email}
              helperText={errors.email || (formData.type === 'buy' ? 'Either email or phone is required' : 'Optional')}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="phone"
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              error={!!errors.phone}
              helperText={errors.phone || (formData.type === 'buy' ? 'Either email or phone is required' : 'Optional')}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.source}>
              <InputLabel>Lead Source *</InputLabel>
              <Select
                name="source"
                value={formData.source}
                onChange={handleChange}
                label="Lead Source *"
                MenuProps={selectMenuProps}
              >
                <MenuItem value="Autotrader">Autotrader</MenuItem>
                <MenuItem value="Car show">Car show</MenuItem>
                <MenuItem value="Cars.com">Cars.com</MenuItem>
                <MenuItem value="Cargurus">Cargurus</MenuItem>
                <MenuItem value="classicars">ClassicCars.com</MenuItem>
                <MenuItem value="Classic">Classic</MenuItem>
                <MenuItem value="Cold call">Cold call</MenuItem>
                <MenuItem value="Cold email">Cold email</MenuItem>
                <MenuItem value="eBay">eBay</MenuItem>
                <MenuItem value="Facebook">Facebook</MenuItem>
                <MenuItem value="Hemmings">Hemmings</MenuItem>
                <MenuItem value="Walk-in">Walk-in</MenuItem>
                <MenuItem value="Website">Website</MenuItem>
              </Select>
              {errors.source && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.source}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Sales Rep</InputLabel>
              <Select
                name="sales_id"
                value={formData.sales_id}
                onChange={handleChange}
                label="Sales Rep"
                MenuProps={selectMenuProps}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {constants.sales.map((salesRep) => (
                  <MenuItem key={salesRep.id} value={salesRep.id}>
                    {salesRep.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Lead Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lead Type *</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                label="Lead Type *"
                MenuProps={selectMenuProps}
              >
                <MenuItem value="buy">Buy</MenuItem>
                <MenuItem value="sell">Sell</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Car They Want to Buy (only for buy leads) */}
          {formData.type === 'buy' && (
          <>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Car They Want to Buy
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TextField
                label="Search for car they want to buy"
                value={carSearchValue}
                onChange={handleCarSearch}
                onKeyDown={handleCarKeyPress}
                fullWidth
                autoComplete="off"
                placeholder="Type to search for a car..."
                InputProps={{
                  endAdornment: selectedCar && (
                    <IconButton
                      onClick={clearSelectedCar}
                      edge="end"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  ),
                }}
              />
              <CarSelectList 
                results={carSearchResults} 
                selected={selectedCarIndex} 
                onSelect={(item, index) => {
                  if (item.action) {
                    item.action();
                  }
                }}
              />
            </Box>
            {selectedCar && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`Selected: ${carSearchValue}`}
                  onDelete={clearSelectedCar}
                  deleteIcon={<DeleteIcon />}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Grid>
          {/* Cars They Own (only for buy leads) */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Cars They Own
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Add car they own"
                value={newCarOwned}
                onChange={(e) => setNewCarOwned(e.target.value)}
                fullWidth
                onKeyPress={(e) => e.key === 'Enter' && addCarOwned()}
                autoComplete="off"
              />
              <Button
                variant="outlined"
                onClick={addCarOwned}
                disabled={!newCarOwned.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            {formData.carsOwned.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.carsOwned.map((car, index) => (
                  <Chip
                    key={index}
                    label={car}
                    onDelete={() => removeCarOwned(index)}
                    deleteIcon={<DeleteIcon />}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            )}
          </Grid>

          {/* Cars They're Interested In (only for buy leads) */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Cars They're Interested In
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Add car they're interested in"
                value={newCarInterested}
                onChange={(e) => setNewCarInterested(e.target.value)}
                fullWidth
                onKeyPress={(e) => e.key === 'Enter' && addCarInterested()}
                autoComplete="off"
              />
              <Button
                variant="outlined"
                onClick={addCarInterested}
                disabled={!newCarInterested.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            {formData.carsInterested.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.carsInterested.map((car, index) => (
                  <Chip
                    key={index}
                    label={car}
                    onDelete={() => removeCarInterested(index)}
                    deleteIcon={<DeleteIcon />}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            )}
          </Grid>
          </>
          )}

          {/* Sell Lead Fields */}
          {formData.type === 'sell' && (
          <>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Car They Want to Sell
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              name="year"
              label="Year *"
              value={formData.year}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.year}
              helperText={errors.year}
              autoComplete="off"
              inputProps={{ maxLength: 4 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              name="make"
              label="Make *"
              value={formData.make}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.make}
              helperText={errors.make}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              name="model"
              label="Model *"
              value={formData.model}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.model}
              helperText={errors.model}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Condition * {errors.condition && (
                <Typography component="span" variant="caption" color="error">
                  {errors.condition}
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {['museum', 'show', 'driver', 'project'].map((conditionValue) => (
                <FormControlLabel
                  key={conditionValue}
                  control={
                    <Checkbox
                      checked={(formData.condition || []).includes(conditionValue)}
                      onChange={() => handleConditionChange(conditionValue)}
                      name={`condition-${conditionValue}`}
                    />
                  }
                  label={conditionValue.charAt(0).toUpperCase() + conditionValue.slice(1)}
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="desiredPrice"
              label="Desired Price"
              type="number"
              value={formData.desiredPrice}
              onChange={handleChange}
              fullWidth
              error={!!errors.desiredPrice}
              helperText={errors.desiredPrice}
              autoComplete="off"
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="thumbnail"
              label="Thumbnail URL"
              type="url"
              value={formData.thumbnail}
              onChange={handleChange}
              fullWidth
              error={!!errors.thumbnail}
              helperText={errors.thumbnail || 'Enter a URL for the car thumbnail image'}
              autoComplete="off"
              placeholder="https://example.com/image.jpg"
            />
          </Grid>

          {/* Thumbnail Preview */}
          {formData.thumbnail && formData.thumbnail.trim() && /^https?:\/\/.+/.test(formData.thumbnail.trim()) && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Card sx={{ maxWidth: 300, width: '100%' }}>
                  {!thumbnailError ? (
                    <CardMedia
                      component="img"
                      image={formData.thumbnail.trim()}
                      alt="Thumbnail preview"
                      sx={{
                        objectFit: 'cover',
                        height: 200,
                      }}
                      onError={() => {
                        setThumbnailError(true);
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body2">Failed to load image</Typography>
                    </Box>
                  )}
                </Card>
              </Box>
            </Grid>
          )}
          </>
          )}

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              name="notes"
              label="Notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              placeholder="Additional notes about this lead..."
              autoComplete="off"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Adding...' : 'Add Lead'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
};

export default AddLeadDialog;